import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type {
  ContractsControllerResponse,
  LockContractEntityRequest,
  LockContractEntityResponse,
  GetContractItemDetailsRequest,
  GetContractItemDetailsResponse,
  ModifyContractItemRequest,
  ModifyContractItemResponse,
  SaveContractAmountRequest,
  SaveContractAmountResponse,
  GetFormMileStonesRequest,
  GetFormMileStonesResponse,
  GetItemBreakUpDetailsRequest,
  GetItemBreakUpDetailsResponse,
  GetRelatedContractsDataForChartRequest,
  GetRelatedContractsDataForChartResponse,
  SyncContractControllerRequest,
  SyncContractControllerResponse,
  GetContractVersionDetailsRequest,
  FetchSummaryOfChangesRequest,
  GetContractLinkedBoqItemsRequest,
  GetContractLinkedBoqItemsResponse,
  UpdateContractTasksRequest,
  UpdateContractTasksResponse,
  GetContractAllVersionsRequest,
  LinkEntitiesToContractRequest,
  LinkEntitiesToContractResponse,
  GetMileStoneControllerResponse,
  ContractWBSControllerResponse,
  ManageContractTemplateResponse,
  ContractsControllerRequest,
  MileStoneControllerRequest,
  ContractWBSControllerRequest,
  ManageContractTemplateRequest,
  GetContractDetailsRequest,
  GetImpFilesRequest,
  syncContractEstimateToLatestVersionRequest,
  syncContractEstimateToLatestVersionResponse,
} from "@ServerTypes/contracts/contracts.schema";
import type {
  GetFormWBSItemsAndAssociatedBoqItemsRequest,
  GetFormWBSItemsAndAssociatedBoqItemsResponse,
  GetFormsDataForChartRequest,
  GetFormsDataForChartResponse,
  GetFormTasksRequest,
  GetFormTasksResponse,
  GetFormBoqItemsRequest,
  GetFormBoqItemsResponse,
  GetContractTaskHierarchyRequest,
  GetContractTaskHierarchyResponse,
} from "@ServerTypes/forms/forms.schema";

import { setContractDetails, type ContractDetailsState } from "./contracts.reducer";
// Each former action-dispatcher is split into dedicated endpoints. These maps route an
// `action`/`entity` to its endpoint path; an unmapped/absent key falls back to the list endpoint.
// The request unions carry `action`/`entity` on some members only and callers hand in a generic
// `T`, so the routing key is read structurally — the union itself never exposes it.
const readAction = (data: unknown): string | undefined =>
  typeof data === "object" && data !== null && "action" in data && typeof data.action === "string"
    ? data.action
    : undefined;
const readEntity = (data: unknown): string | undefined =>
  typeof data === "object" && data !== null && "entity" in data && typeof data.entity === "string"
    ? data.entity
    : undefined;
const CONTRACT_ACTION_PATHS: Record<string, string> = {
  insert: "/contracts/insertContract",
  update: "/contracts/updateContract",
  delete: "/contracts/deleteContract",
  manageModules: "/contracts/manageContractModules",
  saveConfiguration: "/contracts/saveContractsConfiguration",
};
const LINK_ENTITY_PATHS: Record<string, string> = {
  estimate: "/contracts/linkEstimateToContract",
  schedule: "/contracts/linkScheduleToContract",
  task: "/contracts/linkTaskToContract",
  boqItems: "/contracts/linkBoqItemsToContract",
};
const MILESTONE_ACTION_PATHS: Record<string, string> = {
  insert: "/contracts/insertMileStone",
  update: "/contracts/updateMileStone",
  delete: "/contracts/deleteMileStones",
};

export async function contractsController<T extends Exact<ContractsControllerRequest, T>>(data: T) {
  const action = readAction(data);
  const path = (action && CONTRACT_ACTION_PATHS[action]) || "/contracts/contractsController";
  const response = await axiosWrapper.post<ContractsControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
}
export function getContractDetails<T extends Exact<GetContractDetailsRequest, T>>(params: T) {
  return async (dispatch: AppDispatch) => {
    const data = {
      value: {
        _id: params.contractId,
        versionId: params.versionId,
      },
    };
    const response = await axiosWrapper.post<ContractsControllerResponse>(
      `${getBackendUrl()}/contracts/contractsController`,
      data,
    );

    if (response.data.result?.[0]) {
      dispatch(setContractDetails(response.data.result[0] as ContractDetailsState));
    }
  };
}
// Dispatches to the dedicated per-entity link endpoints (default entity = files).
export const linkEntitiesToContract = async <T extends Exact<LinkEntitiesToContractRequest, T>>(data: T) => {
  const entity = readEntity(data);
  const path = (entity && LINK_ENTITY_PATHS[entity]) || "/contracts/linkFilesToContract";
  const response = await axiosWrapper.post<LinkEntitiesToContractResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const lockContractEntity = async <T extends Exact<LockContractEntityRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<LockContractEntityResponse>(
    `${getBackendUrl()}/contracts/lockContractEntity`,
    data,
  );
  return response;
};
export const getContractItemDetails = async <T extends Exact<GetContractItemDetailsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetContractItemDetailsResponse>(
    `${getBackendUrl()}/contracts/getContractItemDetails`,
    data,
  );
  return response;
};
// Dedicated write endpoint (split from the get* read path). The former action:"modify" callers
// post here; the `action` field is dropped and forced server-side.
export const modifyContractItem = async <T extends Exact<ModifyContractItemRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ModifyContractItemResponse>(
    `${getBackendUrl()}/contracts/modifyContractItem`,
    data,
  );
  return response;
};
// Dispatches to the dedicated milestone endpoints (split from mileStoneController).
export const mileStoneController = async <T extends Exact<MileStoneControllerRequest, T>>(data: T) => {
  const action = readAction(data);
  const path = (action && MILESTONE_ACTION_PATHS[action]) || "/contracts/mileStoneController";
  const response = await axiosWrapper.post<GetMileStoneControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const saveContractAmount = async <T extends Exact<SaveContractAmountRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SaveContractAmountResponse>(
    `${getBackendUrl()}/contracts/saveContractAmount`,
    data,
  );
  return response;
};
export const getFormMileStones = async <T extends Exact<GetFormMileStonesRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetFormMileStonesResponse>(
    `${getBackendUrl()}/contracts/getFormMileStones`,
    data,
  );
  return response;
};
// Dispatches to the dedicated WBS endpoints (split from the dynamic contractWBSController).
const WBS_ACTION_PATHS: Record<string, string> = {
  linkWBSItems: "/contracts/linkWBSItems",
  unLinkWBSItems: "/contracts/unLinkWBSItems",
  getAlFamilyOfCDBItems: "/contracts/getAlFamilyOfCDBItems",
  getAllFamilyOfEstimateItems: "/contracts/getAllFamilyOfEstimateItems",
  getWBSItemDetails: "/contracts/getWBSItemDetails",
  saveWBSItemDetails: "/contracts/saveWBSItemDetails",
  manageItemsBudgetAmount: "/contracts/manageItemsBudgetAmount",
  manageBoqViewItems: "/contracts/manageBoqViewItems",
  saveTemplateData: "/contracts/saveContractWBSTemplateData",
};
export const contractWBSController = async <T extends Exact<ContractWBSControllerRequest, T>>(data: T) => {
  const action = readAction(data);
  const path = (action && WBS_ACTION_PATHS[action]) || "/contracts/contractWBSController";
  const response = await axiosWrapper.post<ContractWBSControllerResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const getItemBreakUpDetails = async <T extends Exact<GetItemBreakUpDetailsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetItemBreakUpDetailsResponse>(
    `${getBackendUrl()}/contracts/getItemBreakUpDetails`,
    data,
  );
  return response;
};
export const getFormWBSItemsAndAssociatedBoqItems = async <
  T extends Exact<GetFormWBSItemsAndAssociatedBoqItemsRequest, T>,
>(
  data: T,
) => {
  const response = await axiosWrapper.post<GetFormWBSItemsAndAssociatedBoqItemsResponse>(
    `${getBackendUrl()}/forms/getFormWBSItemsAndAssociatedBoqItems`,
    data,
  );
  return response;
};
// Dispatches to the dedicated template endpoints (default = saveTemplateData).
const MANAGE_TEMPLATE_ACTION_PATHS: Record<string, string> = {
  addTemplate: "/contracts/addContractTemplate",
  removeTemplate: "/contracts/removeContractTemplate",
  saveTemplateData: "/contracts/saveContractTemplateData",
};
export const manageContractTemplate = async <T extends Exact<ManageContractTemplateRequest, T>>(data: T) => {
  const action = readAction(data);
  const path = (action && MANAGE_TEMPLATE_ACTION_PATHS[action]) || "/contracts/saveContractTemplateData";
  const response = await axiosWrapper.post<ManageContractTemplateResponse>(`${getBackendUrl()}${path}`, data);
  return response;
};
export const getFormsDataForChart = async <T extends Exact<GetFormsDataForChartRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetFormsDataForChartResponse>(
    `${getBackendUrl()}/forms/getFormsDataForChart`,
    data,
  );
  return response;
};
export const getRelatedContractsDataForChart = async <T extends Exact<GetRelatedContractsDataForChartRequest, T>>(
  data: T,
) => {
  const response = await axiosWrapper.post<GetRelatedContractsDataForChartResponse>(
    `${getBackendUrl()}/contracts/getRelatedContractsDataForChart`,
    data,
  );
  return response;
};
export const getFormTasksAndAssociatedBoqItems = async <T extends Exact<GetFormTasksRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetFormTasksResponse>(`${getBackendUrl()}/forms/getFormTasks`, data);
  return response;
};
export const getFormBoqItems = async <T extends Exact<GetFormBoqItemsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetFormBoqItemsResponse>(`${getBackendUrl()}/forms/getFormBoqItems`, data);
  return response;
};
export const syncContractController = async <T extends Exact<SyncContractControllerRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SyncContractControllerResponse>(
    `${getBackendUrl()}/contracts/syncContractController`,
    data,
  );
  return response;
};
export const getContractVersionDetails = async <T extends Exact<GetContractVersionDetailsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/getContractVersionDetails`,
    data,
  );
  return response;
};
export const createMirrorOfLatestContractVersion = async <T extends Exact<GetContractAllVersionsRequest, T>>(
  data: T,
) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/createMirrorOfLatestContractVersion`,
    data,
  );
  return response;
};
export const revertContractDraftMirror = async <T extends Exact<GetImpFilesRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/revertContractDraftMirror`,
    data,
  );
  return response;
};
export const syncContractEstimateToLatestVersion = async <
  T extends Exact<syncContractEstimateToLatestVersionRequest, T>,
>(
  data: T,
) => {
  const response = await axiosWrapper.post<syncContractEstimateToLatestVersionResponse>(
    `${getBackendUrl()}/contracts/syncContractEstimateToLatestVersion`,
    data,
  );
  return response;
};
export const validateAndApplyDraftChangesIntoNewVersion = async <T extends Exact<GetImpFilesRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/validateAndApplyDraftChangesIntoNewVersion`,
    data,
  );
  return response;
};

export const getContractTaskHierarchy = async <T extends Exact<GetContractTaskHierarchyRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetContractTaskHierarchyResponse>(
    `${getBackendUrl()}/forms/getContractTaskHierarchy`,
    data,
  );
  return response;
};
export const fetchSummaryOfChanges = async <T extends Exact<FetchSummaryOfChangesRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/fetchSummaryOfChanges`,
    data,
  );
  return response;
};
export const getContractLinkedBoqItems = async <T extends Exact<GetContractLinkedBoqItemsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetContractLinkedBoqItemsResponse>(
    `${getBackendUrl()}/contracts/getContractLinkedBoqItems`,
    data,
  );
  return response;
};
export const updateContractTasks = async <T extends Exact<UpdateContractTasksRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<UpdateContractTasksResponse>(
    `${getBackendUrl()}/contracts/updateContractTasks`,
    data,
  );
  return response;
};
export const getContractAllVersions = async <T extends Exact<GetContractAllVersionsRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<ContractsControllerResponse>(
    `${getBackendUrl()}/contracts/getContractAllVersions`,
    data,
  );
  return response;
};
